import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FuelSelector from './FuelSelector.jsx';

// Reset Leaflet's default divIcon styles
if (typeof document !== 'undefined' && !document.getElementById('fv-hubmap-css')) {
  const s = document.createElement('style');
  s.id = 'fv-hubmap-css';
  s.textContent = `
    .leaflet-div-icon { background: transparent !important; border: none !important; padding: 0 !important; }
    .fv-port-marker   { background: transparent !important; border: none !important; }
    .leaflet-container { border-radius: var(--radius-lg, 10px); overflow: hidden; }
  `;
  document.head.appendChild(s);
}

// ─── Port coordinates ─────────────────────────────────────────────────────────
const PORTS = [
  { name: 'Singapore',       lat: 1.2644,   lng: 103.8198  },
  { name: 'Rotterdam',       lat: 51.9225,  lng: 4.4792    },
  { name: 'Houston',         lat: 29.7604,  lng: -95.3698  },
  { name: 'Fujairah',        lat: 25.1164,  lng: 56.3414   },
  { name: 'Hong Kong',       lat: 22.3193,  lng: 114.1694  },
  { name: 'New York',        lat: 40.6892,  lng: -74.0445  },
  { name: 'Santos',          lat: -23.9608, lng: -46.3336  },
  { name: 'LA / Long Beach', lat: 33.7395,  lng: -118.2600 },
];

// Per-port label direction so labels don't overlap nearby dots
const PORT_LABEL_OFFSETS = {
  'Singapore':       { direction: 'right',  offsetX: 15, offsetY: 0  },
  'Rotterdam':       { direction: 'top',    offsetX: 0,  offsetY: 12 },
  'Houston':         { direction: 'bottom', offsetX: 0,  offsetY: 12 },
  'Fujairah':        { direction: 'top',    offsetX: 0,  offsetY: 12 },
  'Hong Kong':       { direction: 'right',  offsetX: 15, offsetY: -8 },
  'New York':        { direction: 'top',    offsetX: 0,  offsetY: 12 },
  'Santos':          { direction: 'left',   offsetX: 15, offsetY: 0  },
  'LA / Long Beach': { direction: 'left',   offsetX: 15, offsetY: 0  },
};

const CHANGE_KEY = { vlsfo: 'vlsfo_change', mgo: 'mgo_change', ifo380: 'ifo380_change' };

const WORLD_BOUNDS = [[-85, -180], [85, 180]];

function makeIcon(portName, price, change) {
  const isDown   = change != null ? change < 0 : null;
  const dotColor = price == null
    ? '#3a4558'
    : isDown === true  ? '#e9a020'
    : isDown === false ? '#ef4444'
    : '#8892a5';
  const priceStr  = price != null ? `$${price}` : '—';
  const shortName = portName === 'LA / Long Beach' ? 'LA/LB' : portName;

  const { direction = 'top', offsetX = 0, offsetY = 0 } = PORT_LABEL_OFFSETS[portName] || {};

  // Build label position CSS based on direction (dot centered at 0,0)
  let labelPos;
  const DOT = 5; // dot radius
  if (direction === 'right') {
    labelPos = `left:${DOT + offsetX}px;top:${offsetY}px;transform:translateY(-50%);`;
  } else if (direction === 'left') {
    labelPos = `left:${-(DOT + offsetX)}px;top:${offsetY}px;transform:translate(-100%,-50%);`;
  } else if (direction === 'top') {
    labelPos = `top:${-(DOT + offsetY)}px;left:${offsetX}px;transform:translate(-50%,-100%);`;
  } else {
    // bottom
    labelPos = `top:${DOT + offsetY}px;left:${offsetX}px;transform:translate(-50%,0);`;
  }

  return L.divIcon({
    className: 'fv-port-marker',
    html: `
      <div style="position:relative;width:0;height:0;overflow:visible;cursor:pointer;">
        <div style="
          position:absolute;${labelPos}
          background:#111a2e;
          border:1px solid ${dotColor};
          border-radius:7px;
          padding:3px 7px;
          white-space:nowrap;
          box-shadow:0 2px 8px rgba(0,0,0,0.5);
          line-height:1.5;
          pointer-events:none;
        ">
          <span style="font-family:'JetBrains Mono', monospace;font-size:10px;color:#8892a5;">${shortName}</span>
          <span style="font-family:'JetBrains Mono', monospace;font-size:11px;font-weight:600;color:${dotColor};margin-left:5px;">${priceStr}</span>
        </div>
        <div style="
          position:absolute;
          transform:translate(-50%,-50%);
          width:10px;height:10px;
          background:${dotColor};
          border-radius:50%;
          box-shadow:0 0 8px ${dotColor};
        "></div>
      </div>
    `,
    iconSize:   [0, 0],
    iconAnchor: [0, 0],
  });
}

// ─── Map invalidator ──────────────────────────────────────────────────────────
// Leaflet sometimes calculates its size before the container finishes layout.
// Calling invalidateSize() after mount syncs it with the real rendered width.
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(id);
  }, [map]);
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function HubMap({ ports, onPortFocus, fuel: fuelProp, onFuelChange }) {
  const [fuelInternal, setFuelInternal] = useState('vlsfo');
  const fuel    = fuelProp    ?? fuelInternal;
  const setFuel = onFuelChange ?? setFuelInternal;
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const lookup = {};
  ports.forEach(p => { lookup[p.port] = p; });

  const mapHeight = isMobile ? 240 : 320;

  const mapNode = (
    <MapContainer
      center={[20, 15]}
      zoom={isMobile ? 1.3 : 1.8}
      zoomControl={false}
      scrollWheelZoom={false}
      dragging={false}
      doubleClickZoom={false}
      touchZoom={false}
      boxZoom={false}
      keyboard={false}
      attributionControl={false}
      maxBounds={WORLD_BOUNDS}
      maxBoundsViscosity={1.0}
      worldCopyJump={true}
      style={{ height: mapHeight, width: '100%' }}
    >
      <MapInvalidator />
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
      />

      {PORTS.map(({ name, lat, lng }) => {
        const port   = lookup[name];
        if (!port) return null;
        const price  = port[fuel];
        const change = port[CHANGE_KEY[fuel]];
        const icon   = makeIcon(name, price, change);

        return (
          <Marker
            key={name}
            position={[lat, lng]}
            icon={icon}
            eventHandlers={{ click: () => onPortFocus?.(name) }}
          />
        );
      })}
    </MapContainer>
  );

  return (
    <div style={{ background: '#111a2e', borderRadius: 10, border: '1px solid #1e2d44', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #1e2d44',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 600, color: '#fff' }}>
            Hub Ports Overview
          </div>
          <div style={{ fontSize: 11, color: '#8892a5', marginTop: 2 }}>
            {/* {PORTS.length} major bunkering hubs · click a port to highlight in table */}
          </div>
        </div>
        <FuelSelector selected={fuel} onChange={setFuel} />
      </div>

      {/* Map — scrollable on mobile */}
      {isMobile ? (
        <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ minWidth: 600, height: mapHeight }}>
            {mapNode}
          </div>
          <div style={{ textAlign: 'center', fontSize: 10, color: '#3a4558', padding: '4px 0', fontFamily: "'JetBrains Mono', monospace" }}>
            ← scroll →
          </div>
        </div>
      ) : (
        <div style={{ height: mapHeight, width: '100%', overflow: 'hidden' }}>
          {mapNode}
        </div>
      )}
    </div>
  );
}
